import React, { useState } from 'react';
import { Target, Users, TrendingUp, Award, ChevronDown, ChevronUp } from 'lucide-react';
import Navbar from '../../components/user/Navbar';
import Footer from '../../components/user/Footer';
import FAQ from '../../components/user/FAQ';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const About: React.FC = () => {
  const [heroRef, heroVisible] = useScrollAnimation();
  const [missionRef, missionVisible] = useScrollAnimation();
  const [valuesRef, valuesVisible] = useScrollAnimation();
  const [learnMoreExpanded, setLearnMoreExpanded] = useState(false);

  return (
    <div className="min-h-screen bg-[#faf8ed]">
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 md:pt-52 pb-20 md:pb-32 px-4 bg-mesh-premium relative overflow-hidden">
        {/* Animated Background Blobs */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none -z-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#0c513f]/5 rounded-full blur-[120px] animate-blob"></div>
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-chowdeck-pastelYellow/20 rounded-full blur-[100px] animate-blob animation-delay-2000"></div>
        </div>

        <div className={`container-premium text-center transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          <h1 className="heading-huge text-[#0c513f] mb-8 tracking-tighter-premium">
            About Rita
          </h1>
          <p className="text-2xl md:text-3xl text-secondary-900 max-w-3xl mx-auto leading-tight font-medium tracking-tight">
            Revolutionizing the way businesses connect with customers through
            seamless QR payment and discovery solutions.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section ref={missionRef} className="py-20 md:py-32 px-4 bg-white rounded-chow-lg -mt-10 relative z-20">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 gap-20 items-center">
            <div className={`space-y-10 transition-all duration-1000 ${missionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-20'}`}>
              <h2 className="text-6xl md:text-7xl font-black text-[#0c513f] tracking-tighter-premium leading-none">Our Mission</h2>
              <div className="space-y-6">
                <p className="text-2xl text-secondary-600 leading-relaxed font-medium">
                  To create a universal platform that empowers businesses of all sizes
                  to embrace digital payments while providing customers with a seamless
                  way to discover and engage with local businesses.
                </p>
                <p className="text-2xl text-secondary-600 leading-relaxed font-medium">
                  We believe in making technology accessible, secure, and beneficial
                  for everyone in the commerce ecosystem.
                </p>
              </div>
            </div>
            <div className={`bg-[#0c513f] rounded-[4rem] p-16 text-white shadow-[0_40px_100px_-20px_rgba(12,81,63,0.3)] transition-all duration-1000 hover:scale-[1.02] ${missionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-20'}`}>
              <Target size={80} className="mb-10 text-[#faf8ed] animate-float" />
              <h3 className="text-5xl font-black mb-8 text-[#faf8ed] tracking-tight">Our Vision</h3>
              <p className="text-2xl text-white/90 leading-relaxed font-medium">
                To become the world's most trusted and user-friendly platform
                for QR-based payments and business discovery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section ref={valuesRef} className="py-20 md:py-32 px-4 bg-[#faf8ed]">
        <div className="container-premium">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-[#0c513f] mb-6 tracking-tighter-premium">
              Our Core Values
            </h2>
            <p className="text-2xl text-secondary-600 font-medium">
              Principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                icon: <Users size={48} />,
                title: 'Customer First',
                description: 'We prioritize user experience and satisfaction above all else.'
              },
              {
                icon: <TrendingUp size={48} />,
                title: 'Innovation',
                description: 'Constantly evolving to meet the changing needs of digital commerce.'
              },
              {
                icon: <Award size={48} />,
                title: 'Excellence',
                description: 'Committed to delivering the highest quality in everything we build.'
              },
              {
                icon: <Target size={48} />,
                title: 'Integrity',
                description: 'Building trust through transparency and ethical practices.'
              }
            ].map((value, index) => (
              <div
                key={index}
                className={`card-chowdeck bg-white p-10 flex flex-col items-center text-center ${valuesVisible
                  ? `animate-fade-in-up opacity-100`
                  : 'opacity-100'
                  }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="text-primary-500 mb-4 flex justify-center hover:scale-110 hover:rotate-3 transition-all duration-300">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-secondary-900 mb-3 hover:text-primary-600 transition-colors">
                  {value.title}
                </h3>
                <p className="text-secondary-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CEO Message Section */}
      <section className="py-20 md:py-32 px-4 bg-white relative z-10">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="relative order-2 md:order-1 max-w-sm mx-auto w-full">
              <div className="absolute -inset-4 bg-[#0c513f]/10 rounded-[3rem] transform -rotate-3 scale-105 z-0"></div>
              <img 
                src="/ceo-new.jpg" 
                alt="Founder and President" 
                className="w-full h-[450px] object-cover object-top bg-gray-50 rounded-[2.5rem] shadow-[0_20px_50px_-10px_rgba(12,81,63,0.2)] relative z-10"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl z-20 border border-gray-100">
                <p className="font-bold text-2xl text-[#0c513f]">Femi Adewumi</p>
                <p className="font-bold text-secondary-600">Founder & President, Rita</p>
              </div>
            </div>
            
            <div className="order-1 md:order-2 space-y-8">
              <h2 className="text-5xl md:text-6xl font-black text-[#0c513f] tracking-tighter-premium leading-tight">
                A Message from our <br /> Founder & President
              </h2>
              <div className="space-y-6 text-xl text-secondary-600 leading-relaxed font-medium">
                <p>
                  "Rita was born from the desire to create a simpler, more connected ecosystem for businesses and their customers. We saw that digital payments could be more than just transactions—they could be a gateway to discovery and growth."
                </p>
                <p>
                  "Our platform is designed to break down barriers, providing businesses of all sizes with the tools they need to thrive in a digital-first world, while giving consumers an effortless way to explore and pay."
                </p>
                <p className="text-secondary-900 font-bold">
                  "Thank you for being part of the Rita community."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-5 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-secondary-900 mt-4 mb-8">
            Built by Experts
          </h2>
          <p className="text-xl text-secondary-600">
            Our team combines decades of experience in fintech,
            e-commerce, and technology innovation.
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Frequently Asked Questions About Rita"
        subtitle="Find answers to common questions about Rita services, features, and how to get started."
        faqs={[
          {
            question: 'What is Rita and how does it work?',
            answer: 'Rita is a universal QR payment and discovery platform that helps customers discover nearby businesses and make instant payments using QR codes. Simply scan a business QR code, navigate to the location, and pay securely through your digital wallet.'
          },
          {
            question: 'Is Rita safe and secure?',
            answer: 'Yes, Rita prioritizes security. All businesses on our platform are KYC verified, and all transactions are encrypted. We use industry-standard security measures to protect your data and financial information.'
          },
          {
            question: 'How can I add my business to Rita?',
            answer: 'To add your business to Rita, you can contact us through our contact page or reach out to our business development team. We\'ll guide you through the registration process, which includes KYC verification and setting up your business profile.'
          },
          {
            question: 'What types of businesses can use Rita?',
            answer: 'Rita is suitable for all types of businesses including retail stores, restaurants, salons, fuel stations, service providers, and more. If you accept payments and want to reach more customers, Rita can help you grow your business.'
          },
          {
            question: 'Are there any fees for using Rita?',
            answer: 'Rita offers transparent pricing with competitive transaction fees. The exact fees depend on your business type and transaction volume. Contact our sales team for detailed pricing information tailored to your business needs.'
          },
          {
            question: 'How can I get support if I have issues?',
            answer: 'We offer comprehensive support through multiple channels. You can reach us via our contact page, email support, or through the in-app support feature. Our support team is available to help with any questions or issues you may have.'
          }
        ]}
        ctaText="Still have questions? We're here to help!"
      />

      {/* Learn More About Rita - SEO Section */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setLearnMoreExpanded(!learnMoreExpanded)}
              className="bg-secondary-200 hover:bg-secondary-300 text-secondary-800 px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2 shadow-sm hover:shadow-md"
            >
              <span>Learn More About Rita</span>
              {learnMoreExpanded ? (
                <ChevronUp size={20} className="transition-transform" />
              ) : (
                <ChevronDown size={20} className="transition-transform" />
              )}
            </button>
          </div>

          <div
            className={`overflow-hidden transition-all duration-500 ${learnMoreExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <div className="max-w-4xl mx-auto space-y-8 text-secondary-700">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-6">
                  Rita - A Universal QR Payment & Discovery Platform
                </h2>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    Rita is a comprehensive digital platform that revolutionizes the way customers discover businesses and make payments.
                    Our universal QR payment and discovery platform offers seamless digital wallet solutions, instant QR code payments,
                    business location services, and real-time navigation. Whether you're looking for nearby stores, restaurants, salons,
                    fuel stations, or service providers, Rita simplifies your journey by combining business discovery, navigation, and
                    payment processing into one intuitive platform.
                  </p>
                  <p>
                    Our mission is to empower businesses of all sizes while providing customers with a smooth, convenient experience.
                    For consumers, Rita offers a seamless way to discover and pay at local businesses without the need for cash or cards.
                    For business owners, we provide cutting-edge point-of-sale systems, comprehensive analytics, and intelligent payment
                    solutions that help grow their customer base and streamline operations. Rita delivers convenience, transparency, and
                    cutting-edge technology through our advanced business management tools, dedicated mobile applications, and secure
                    payment infrastructure.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Key Features of Rita
                </h3>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Digital Wallet
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's secure and intuitive digital wallet allows users to hold funds, make instant payments, and track all
                      transactions in real-time. With our wallet, you can easily manage your finances, view transaction history,
                      and enjoy the convenience of scanning QR codes instead of carrying cash or cards. The wallet is protected
                      by advanced encryption and security measures to ensure your money and personal information remain safe.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      QR Code Payment System
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Our innovative QR code payment system enables instant, contactless transactions at any business partner.
                      Simply scan the QR code displayed at the business location, confirm the amount, and complete your payment
                      securely through your digital wallet. This eliminates the need for physical cash or credit cards, making
                      transactions faster, safer, and more convenient for both customers and businesses.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Discovery & Navigation
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita's powerful discovery platform helps you find nearby businesses based on your location, preferences,
                      and search criteria. Our integrated mapping system provides real-time navigation, directions, and business
                      information including hours, ratings, and available services. Whether you're looking for a specific store,
                      restaurant, or service provider, Rita makes it easy to discover and navigate to your destination.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Business Analytics & Management
                    </h4>
                    <p className="text-lg leading-relaxed">
                      For business owners, Rita offers comprehensive analytics and management tools to track performance, monitor
                      transactions, and gain valuable insights into customer behavior. Our dashboard provides real-time reports,
                      sales analytics, customer demographics, and performance metrics to help you make data-driven decisions and
                      grow your business effectively.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Security & Verification
                    </h4>
                    <p className="text-lg leading-relaxed">
                      All businesses on the Rita platform undergo KYC (Know Your Customer) verification to ensure authenticity
                      and security. Our platform uses industry-standard encryption protocols to protect all transactions and
                      personal data. With Rita, you can trust that every transaction is secure, verified, and protected by
                      advanced security measures.
                    </p>
                  </div>

                  <div>
                    <h4 className="text-xl font-bold text-secondary-900 mb-3">
                      Multi-Category Support
                    </h4>
                    <p className="text-lg leading-relaxed">
                      Rita supports businesses across multiple categories including retail stores, restaurants, cafes, salons,
                      fuel stations, service providers, healthcare facilities, and more. Our platform is designed to accommodate
                      various business types and industries, making it a versatile solution for businesses of all sizes and sectors.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  How Rita Benefits You
                </h3>
                <div className="space-y-4 text-lg leading-relaxed">
                  <p>
                    <strong className="text-secondary-900">For Customers:</strong> Rita provides a seamless, cashless payment
                    experience with the convenience of discovering and navigating to nearby businesses. Our platform saves time,
                    reduces the need for physical currency, and offers a secure way to manage your transactions and explore local
                    businesses.
                  </p>
                  <p>
                    <strong className="text-secondary-900">For Businesses:</strong> Rita helps businesses reach more customers,
                    streamline payment processing, and gain valuable insights through our analytics platform. Our tools enable
                    businesses to accept digital payments easily, track performance, and grow their customer base with minimal
                    setup and maintenance.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                  Getting Started with Rita
                </h3>
                <p className="text-lg leading-relaxed">
                  Getting started with Rita is simple and straightforward. Download our mobile application, create your account,
                  and set up your digital wallet. For businesses, our onboarding process includes KYC verification, business profile
                  setup, and QR code generation. Our support team is available to assist you throughout the process, ensuring a
                  smooth and hassle-free experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;

